import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemeMode = 'light' | 'dark' | 'auto';

const THEME_KEY = 'app-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly themeSubject: BehaviorSubject<ThemeMode>;
  readonly theme$: Observable<ThemeMode>;

  private resolvedTheme: 'light' | 'dark' = 'light';
  private mediaQuery: MediaQueryList;

  private themeChangeCallback: ((theme: 'light' | 'dark') => void) | null = null;

  constructor() {
    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const saved = this.loadSaved();
    this.themeSubject = new BehaviorSubject<ThemeMode>(saved);
    this.theme$ = this.themeSubject.asObservable();

    this.applyTheme(saved);

    this.mediaQuery.addEventListener('change', () => {
      if (this.themeSubject.value === 'auto') {
        this.applyTheme('auto');
      }
    });
  }

  onThemeChange(callback: (theme: 'light' | 'dark') => void): void {
    this.themeChangeCallback = callback;
    callback(this.resolvedTheme);
  }

  get currentTheme(): ThemeMode {
    return this.themeSubject.value;
  }

  get effectiveTheme(): 'light' | 'dark' {
    return this.resolvedTheme;
  }

  setTheme(mode: ThemeMode): void {
    localStorage.setItem(THEME_KEY, mode);
    this.themeSubject.next(mode);
    this.applyTheme(mode);
  }

  toggle(): void {
    const next = this.resolvedTheme === 'light' ? 'dark' : 'light';
    this.setTheme(next);
  }

  private loadSaved(): ThemeMode {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'auto') {
      return saved;
    }
    return 'light';
  }

  private applyTheme(mode: ThemeMode): void {
    this.resolvedTheme = mode === 'auto'
      ? (this.mediaQuery.matches ? 'dark' : 'light')
      : mode;

    document.documentElement.setAttribute('data-bs-theme', this.resolvedTheme);
    document.body.setAttribute('data-layout-mode', this.resolvedTheme);

    if (this.themeChangeCallback) {
      this.themeChangeCallback(this.resolvedTheme);
    }
  }
}
